<template>
    <v-container fluid class="container-hero px-0 py-6 py-lg-12 mb-6 mb-lg-12">
        <v-container>
            <slot name="notification"></slot>
            <v-card 
                color="white" 
                class="custom-radius tutor-card"
                flat
            >
                <v-row>
                    <v-col 
                        cols="12"
                        lg="3"
                    >
                        <v-card-text class="justify-center">
                            <div 
                                class="image-container" 
                                :style="`background-image: url('${photo}');`"
                            ></div>
                            <v-row no-gutters justify="center" v-if="rating > 0">
                                <div class="title mr-2" v-text="rating"></div>
                                <v-col cols="12" class="d-flex justify-center">
                                    <v-rating
                                        :value="rating"
                                        dense
                                        half-increments
                                        background-color="orange lighten-3"
                                        color="orange"
                                        readonly
                                        size="24"
                                    ></v-rating>
                                </v-col>
                                <v-col cols="12" class="d-flex justify-center"><div class="subtitle-1 font-weight-medium">{{ review }} {{ $t('Reviews') }}</div></v-col>
                            </v-row>
                        </v-card-text>                
                    </v-col>
                    <v-col sm="12" md="12" lg="8">
                        <v-card-text class="grey--text text--darken-1">
                            <h1 class="display-1 font-weight-bold my-1" v-text="fullName"></h1>
                            <div class="subtitle-1 mb-4" v-if="country" v-text="`(${country.name})`"></div>
                            <v-card flat color="grey lighten-4" class="mb-2">
                                <v-card-text>
                                    <span class="primary--text font-weight-bold" v-text="`${$t('Biography')}:`"></span> 
                                    <template v-if="bio">
                                        {{ bio }}
                                    </template>
                                    <template v-else> -</template>
                                </v-card-text>
                            </v-card>
                            <v-card flat color="grey lighten-4" class="mb-2">
                                <v-card-text>
                                    <span class="primary--text font-weight-bold" v-text="`${$t('Education')}:`"></span> 
                                    <template v-if="education">
                                        {{ education }}
                                    </template>
                                    <template v-else> -</template>
                                </v-card-text>
                            </v-card>
                            <v-card flat color="grey lighten-4" class="mb-2">
                                <v-card-text>
                                    <span class="primary--text font-weight-bold" v-text="`${$t('TeachingExperience')}:`"></span> 
                                    <template v-if="teachingExperience">
                                        {{ teachingExperience }}
                                    </template>
                                    <template v-else> -</template>
                                </v-card-text>
                            </v-card>
                            <template v-if="subjects.length > 0">
                                <div class="mt-4 mb-2">
                                    <span class="subtitle-2" v-text="`${$t('Subjects')}: `"></span>
                                    <v-chip 
                                        outlined
                                        :small="$vuetify.breakpoint.smAndDown"
                                        v-for="(subject, index) in subjects" 
                                        :key="index" 
                                        v-text="subject.label"
                                        class="ma-1"
                                        color="primary"
                                    >
                                    </v-chip>
                                </div>
                            </template>
                            <template v-if="selectedLanguages.length > 0">
                                <div class="my-2">
                                    <span class="subtitle-2" v-text="`${$t('Languages')}: `"></span>
                                    <v-chip 
                                        outlined
                                        :small="$vuetify.breakpoint.smAndDown"
                                        v-for="(language, index) in selectedLanguages" 
                                        :key="index" 
                                        v-text="getLanguageById(language.languageId)"
                                        class="ma-1"
                                        color="primary"
                                    ></v-chip>
                                </div>
                            </template>
                            <template v-if="cv">
                                <v-btn text color="primary" target="_blannk" :href="`${baseImageUrl}${cv}`">
                                    <v-icon left>mdi-paperclip</v-icon>
                                    {{ $t('Download') }}
                                </v-btn>
                            </template>                            
                        </v-card-text>
                    </v-col>
                </v-row>
            </v-card>
            <slot name="action"></slot>
        </v-container>
    </v-container>
</template>

<script>
    import { mapGetters } from 'vuex'

    export default {
        name: 'tutorHero',
        props: {
            id: Number,
            firstName: String,
            lastName: String,
            bio: String,
            education: String,
            teachingExperience: String,
            photo: String,
            country: {
                type: Object,
                default: () => ({
                    id: 0,
                    label: '',
                    name: '',
                })
            },
            subjects: {
                type: Array,
                default: () => []
            },
            rating: Number,
            review: Number,
            cv: {
                type: String,
                default: null
            },
            selectedLanguages: {
                type: Array,
                default: () => []
            }
        },
        computed: {
            ...mapGetters(['baseImageUrl', 'languages']),
            fullName() {
                return this.firstName + ' ' + this.lastName
            }
        },
        methods: {
            getLanguageById(id) {
                let index = this.languages.map(language => {
                    return language.id
                }).indexOf(id)
                return this.languages[index].label
            }
        }
    }
</script>

<style lang="scss" scoped>
    .container-hero{
        background: rgb(86,125,251);
        background: -moz-linear-gradient(top, rgba(86,125,251,1) 0%, rgba(56,217,253,1) 100%);
        background: -webkit-linear-gradient(top, rgba(86,125,251,1) 0%,rgba(56,217,253,1) 100%);
        background: linear-gradient(to bottom, rgba(86,125,251,1) 0%,rgba(56,217,253,1) 100%);
        filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#567dfb', endColorstr='#38d9fd',GradientType=0 );
    }
    .image-container{
        width: 180px;
        height: 180px;
        margin: 15px auto;
        background-color: $lightGrey;
        background-size: cover;
        background-repeat: no-repeat;
        background-position: top center;
        border-radius: 100%;
    }
    .tutor-card{
        box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.1), 0px 6px 10px 0px rgba(0, 0, 0, 0.07), 0px 1px 18px 0px rgba(0, 0, 0, 0.06) !important;
    }
</style>